import { Suspense, lazy, useEffect } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { Layout } from './components/Layout'
import { Spinner } from './components/ui'
import { useAccountSync } from './lib/queries'
import { AddExpensePage } from './pages/AddExpense'
import { CategoriesPage } from './pages/Categories'
import { DashboardPage } from './pages/Dashboard'
import { ExpenseDetailPage } from './pages/ExpenseDetail'
import { ImportPage } from './pages/Import'
import { LoginPage } from './pages/Login'
import { MembersPage } from './pages/Members'
import { PairDetailPage } from './pages/PairDetail'
import { ProfilePage } from './pages/Profile'
import { useSession } from './session'

/**
 * The charts are a third of the bundle and only one screen draws them, so they
 * load when that screen is opened rather than on the way to the dashboard -- the
 * screen a user reaches on every single launch.
 */
const ReportsPage = lazy(async () => ({ default: (await import('./pages/Reports')).ReportsPage }))

export function App() {
  return (
    <>
      <AutoUpdate />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RequireSession />}>
          <Route element={<Layout />}>
            <Route index path="/" element={<DashboardPage />} />
            <Route path="/add" element={<AddExpensePage />} />
            <Route path="/expenses/:expenseId" element={<ExpenseDetailPage />} />
            <Route
              path="/reports"
              element={
                <Suspense fallback={<Spinner />}>
                  <ReportsPage />
                </Suspense>
              }
            />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/import" element={<ImportPage />} />
            <Route path="/members" element={<MembersPage />} />
            <Route path="/members/:pairId" element={<PairDetailPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

/**
 * Gate every authenticated route.
 *
 * The redirect carries the attempted path, so a shared link to a split still
 * lands on that split after signing in rather than dumping the user on the home
 * screen with no idea what they were opening.
 */
function RequireSession() {
  const { session } = useSession()
  const location = useLocation()
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }
  return (
    <>
      {/* Mounted here rather than in a page, so it runs on whichever screen the
          app happens to open on, and stays mounted across navigation -- see
          `useAccountSync`. */}
      <AccountSync />
      <Outlet />
    </>
  )
}

function AccountSync() {
  useAccountSync()
  return null
}

/**
 * Screens holding input that a reload would throw away.
 *
 * Route prefixes, so `/expenses/<id>` and `/members/<pairId>` are covered: both
 * edit in place. Everything else -- the dashboard, reports, the account screen --
 * is read-only, which is also where the app sits idle most of the time.
 */
const SCREENS_WITH_UNSAVED_INPUT = ['/add', '/import', '/categories', '/expenses/', '/members']

/**
 * Apply a new bundle by itself, without asking.
 *
 * `registerType` stays `'prompt'` so that *when* is ours to choose, not so that a
 * banner can ask: applying an update reloads the page, and a reload in the middle
 * of a half-typed expense loses the typing. So it waits for a screen where there is
 * nothing to lose. Navigating off 記帳 is what releases it, which in practice means
 * the update lands within a tap or two of the deploy and the user never sees it.
 */
function AutoUpdate() {
  const {
    needRefresh: [needsRefresh],
    updateServiceWorker,
  } = useRegisterSW()
  const { pathname } = useLocation()

  const safeToReload = !SCREENS_WITH_UNSAVED_INPUT.some((prefix) => pathname.startsWith(prefix))
  useEffect(() => {
    if (needsRefresh && safeToReload) void updateServiceWorker(true)
  }, [needsRefresh, safeToReload, updateServiceWorker])

  return null
}
